import { useEffect } from 'react';
import {
    LocalParticipant,
    Participant,
    ParticipantEvent,
    RemoteTrack,
    RemoteTrackPublication,
    Track,
    TrackPublication,
    VideoQuality,
} from 'livekit-client';
import { setLocalCameraQuality } from 'utils/videoQualityHelpers';
import { createLogger } from '@web-chat-core/toolkit';

const logger = createLogger({ prefix: 'WebRTC Optimization' });

/**
 * Устанавливает HIGH качество для спикера при подписке на его трек
 * Логика понижения качества предыдущего спикера вынесена в useVideoQualityByParticipantCount
 */
export const useSpeakerTrackQuality = (participantInFocus?: Participant) => {
    useEffect(() => {
        if (!participantInFocus) return;

        // Для локального спикера качество управляется через useVideoQualityByParticipantCount
        if (participantInFocus.isLocal && participantInFocus instanceof LocalParticipant) {
            setLocalCameraQuality(participantInFocus, VideoQuality.HIGH, participantInFocus.identity);
            return;
        }

        const setHighQuality = (publication?: TrackPublication | null) => {
            if (
                !publication ||
                publication.source !== Track.Source.Camera ||
                !(publication instanceof RemoteTrackPublication) ||
                !publication.isSubscribed
            ) {
                return;
            }

            try {
                if (publication.setVideoQuality) {
                    publication.setVideoQuality(VideoQuality.HIGH);
                }
            } catch (error) {
                logger.warn(`Failed to set HIGH quality for ${participantInFocus.identity}:`, error);
            }
        };

        // Устанавливаем HIGH качество для существующего трека
        const cameraPublication = participantInFocus.getTrackPublication(Track.Source.Camera);
        if (cameraPublication) {
            setHighQuality(cameraPublication);
        }

        // Устанавливаем HIGH качество при подписке на новый трек
        const handleTrackSubscribed = (_track: RemoteTrack, publication: TrackPublication) => {
            setHighQuality(publication);
        };

        participantInFocus.on(ParticipantEvent.TrackSubscribed, handleTrackSubscribed);

        return () => {
            participantInFocus.off(ParticipantEvent.TrackSubscribed, handleTrackSubscribed);
        };
    }, [participantInFocus]);
};
